import {
	NavigationAction,
	RouteProp,
	useNavigation,
	usePreventRemove,
	useRoute,
} from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, ImageBackground, Keyboard, StyleSheet, Text, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';

import {
	DuelCalendarPng,
	DuelGiftBoxPng,
	DuelIconClockSvg,
	DuelIconEmailPng,
	DuelIconRequestIdPng,
	DuelRealMoneyPng,
	DuelRealWorldRewardPng,
	DuelRequestSubmittedBgPng,
	DuelStarPng,
	DuelSuccessCheckPng,
	DuelSuccessConfettiPng,
	DuelSuccessGlowPng,
} from '../../assets/duels';
import type { DuelPlayerRewardDto, DuelRewardMethod } from '../../services/api';
import {
	duelRewardCancelSubmit,
	duelRewardRequestSubmit,
	duelRewardTransitionReset,
	duelsRewardsRequest,
} from '../../store/duels/duelsActions';
import {
	selectDuelReward,
	selectDuelRewardOverlays,
	selectDuelRewardsErrorKey,
	selectDuelRewardsLoaded,
	selectDuelRewardsLoading,
	selectDuelRewardTransition,
} from '../../store/duels/duelsReducer';
import {
	DuelDetailRow,
	DuelEmailField,
	DuelInfoBox,
	DuelMethodOption,
	DuelRewardBanner,
	DuelSection,
} from '../Components/Duels/DuelRedeemUi';
import { markDuelRewardSeen } from '../Components/Duels/duelRewardSeen';
import {
	DUEL_REWARD_METHODS,
	DuelRewardStatusPill,
	formatShortDate,
	lastRequestedAt,
	lastRequestMethod,
	rewardMethodText,
	rewardPillText,
	rewardRequestId,
	rewardStatusInfo,
	rewardStatusText,
	rewardTimeline,
} from '../Components/Duels/DuelRewardUi';
import { duelText } from '../Components/Duels/duelsText';
import { formatScore } from '../Components/Duels/duelsTheme';
import {
	DuelButton,
	DuelLoading,
	DuelPanel,
	DuelsPage,
	duelTextStyles,
} from '../Components/Duels/DuelsUi';
import { RootStackParamList } from '../navigator';

type NavType = NativeStackNavigationProp<RootStackParamList>;
type RewardRoute = RouteProp<RootStackParamList, 'DuelReward'>;
type Step = 'details' | 'form' | 'submitted';

const EMAIL_MAX = 200;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const REQUEST_STATES = ['Requested', 'UnderReview', 'Approved', 'Rejected', 'Fulfilled', 'Failed'];

const DuelRewardScreen = () => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const navigation = useNavigation<NavType>();
	const { entitlementId } = useRoute<RewardRoute>().params;
	const selectReward = useMemo(() => selectDuelReward(entitlementId), [entitlementId]);
	const reward = useSelector(selectReward);
	const overlays = useSelector(selectDuelRewardOverlays);
	const loaded = useSelector(selectDuelRewardsLoaded);
	const loadErrorKey = useSelector(selectDuelRewardsErrorKey);
	const rewardsLoading = useSelector(selectDuelRewardsLoading);
	const transition = useSelector(selectDuelRewardTransition);

	const [step, setStep] = useState<Step>('details');
	const [method, setMethod] = useState<DuelRewardMethod>('PayPal');
	const [email, setEmail] = useState('');
	const [submitted, setSubmitted] = useState(false);

	useEffect(() => {
		dispatch(duelRewardTransitionReset());
		dispatch(duelsRewardsRequest());
		markDuelRewardSeen(entitlementId).catch(() => {});
	}, [dispatch, entitlementId]);

	const submittedHere = useRef(false);
	useEffect(() => {
		if (!submittedHere.current) return;
		if (transition.doneId === entitlementId && transition.doneKind === 'request') {
			setStep('submitted');
		}
	}, [entitlementId, transition]);

	const blockedAction = useRef<NavigationAction | null>(null);
	usePreventRemove(step === 'form', ({ data }) => {
		if (!['GO_BACK', 'POP'].includes(data.action.type)) blockedAction.current = data.action;
		setStep('details');
	});
	useEffect(() => {
		if (step === 'form' || !blockedAction.current) return;
		const action = blockedAction.current;
		blockedAction.current = null;
		navigation.dispatch(action);
	}, [navigation, step]);

	const close = () => navigation.goBack();
	const back = () => (step === 'form' ? setStep('details') : close());
	const openInventory = () => {
		navigation.goBack();
		navigation.navigate('Inventory', { tab: 'inventory', category: 'rewards' });
	};

	const emailValid = EMAIL_PATTERN.test(email.trim()) && email.trim().length <= EMAIL_MAX;
	const submit = () => {
		setSubmitted(true);
		if (!emailValid || transition.submitting) return;
		Keyboard.dismiss();
		submittedHere.current = true;
		dispatch(duelRewardRequestSubmit({ entitlementId, method, email: email.trim() }));
	};

	const inRequestFlow = (current: DuelPlayerRewardDto) =>
		REQUEST_STATES.includes(current.status) || current.canCancel;
	const amountOf = (current: DuelPlayerRewardDto) => current.title ?? formatScore(current.value);
	const shortAmountOf = (current: DuelPlayerRewardDto) =>
		current.title?.match(/^\S*\d[\d.,]*\S*/)?.[0] ?? formatScore(current.value);
	const milestoneText = (current: DuelPlayerRewardDto) =>
		duelText(t, 'T_SITE_DUELS_SEASON_POINTS_VALUE', [formatScore(current.threshold)]);
	const statusPill = (current: DuelPlayerRewardDto, scale: number) => (
		<DuelRewardStatusPill
			status={current.status}
			scale={scale}
			label={rewardPillText(t, current.status)}
		/>
	);

	const errorLine = (scale: number) =>
		transition.errorKey ? (
			<Text style={[styles.error, { fontSize: 12 * scale }]}>
				{duelText(t, transition.errorKey)}
			</Text>
		) : null;

	const pageTitle = () => {
		if (step === 'form') return duelText(t, 'T_SITE_DUELS_PAGE_REQUEST_REWARD');
		if (step === 'submitted' || (reward && inRequestFlow(reward))) {
			return duelText(t, 'T_SITE_DUELS_PAGE_REWARD_REQUEST');
		}
		return duelText(t, 'T_SITE_DUELS_PAGE_REWARD_DETAILS');
	};

	const renderDetails = (current: DuelPlayerRewardDto, width: number, scale: number) => {
		const requestFlow = inRequestFlow(current);
		const timeline = rewardTimeline(current.history);
		const info = rewardStatusInfo(t, current.status);
		const requestMethod = overlays[current.id] ? null : lastRequestMethod(current.history);

		return (
			<>
				<DuelRewardBanner
					seasonName={current.seasonName}
					amount={shortAmountOf(current)}
					tag={duelText(t, 'T_SITE_DUELS_REAL_WORLD_REWARD').toUpperCase()}
					scale={scale}
					width={width}
				/>
				{!requestFlow ? (
					<Text style={[styles.unlockLine, { width, fontSize: 12 * scale }]}>
						{duelText(t, 'T_SITE_DUELS_REWARD_UNLOCKED_BY', [
							formatScore(current.threshold),
							current.seasonName,
						])}
					</Text>
				) : null}

				{requestFlow ? (
					<DuelSection
						title={duelText(t, 'T_SITE_DUELS_REQUEST_DETAILS_TITLE')}
						scale={scale}
						width={width}
					>
						<DuelDetailRow
							icon={DuelIconRequestIdPng}
							label={duelText(t, 'T_SITE_DUELS_ROW_REQUEST_ID')}
							value={rewardRequestId(current.id)}
							scale={scale}
						/>
						{requestMethod ? (
							<DuelDetailRow
								icon={DuelRealMoneyPng}
								label={duelText(t, 'T_SITE_DUELS_ROW_METHOD')}
								value={rewardMethodText(t, requestMethod)}
								scale={scale}
							/>
						) : null}
						<DuelDetailRow
							icon={DuelCalendarPng}
							label={duelText(t, 'T_SITE_DUELS_ROW_REQUESTED')}
							value={formatShortDate(lastRequestedAt(current.history))}
							scale={scale}
						/>
						<DuelDetailRow
							icon={<DuelIconClockSvg width={22 * scale} height={22 * scale} />}
							label={duelText(t, 'T_SITE_DUELS_ROW_STATUS')}
							value={statusPill(current, scale)}
							scale={scale}
						/>
					</DuelSection>
				) : null}

				{requestFlow && timeline.length ? (
					<DuelSection
						title={duelText(t, 'T_SITE_DUELS_STATUS_SECTION')}
						scale={scale}
						width={width}
					>
						{timeline.map(entry => (
							<View key={`${entry.status}-${entry.at}`} style={styles.timelineRow}>
								<Text style={[duelTextStyles.body, { fontSize: 13 * scale }]}>
									{`● ${rewardStatusText(t, entry.status)}`}
								</Text>
								<Text style={[duelTextStyles.muted, { fontSize: 11 * scale }]}>
									{formatShortDate(entry.at, false)}
								</Text>
							</View>
						))}
					</DuelSection>
				) : null}

				<DuelSection
					title={duelText(t, 'T_SITE_DUELS_REWARD_DETAILS_TITLE')}
					scale={scale}
					width={width}
				>
					<DuelDetailRow
						icon={DuelStarPng}
						label={duelText(t, 'T_SITE_DUELS_ROW_MILESTONE')}
						value={milestoneText(current)}
						scale={scale}
					/>
					<DuelDetailRow
						icon={DuelCalendarPng}
						label={duelText(t, 'T_SITE_DUELS_ROW_SEASON')}
						value={current.seasonName}
						scale={scale}
					/>
					<DuelDetailRow
						icon={DuelGiftBoxPng}
						label={duelText(t, 'T_SITE_DUELS_ROW_REWARD_TYPE')}
						value={duelText(t, 'T_SITE_DUELS_REAL_WORLD_REWARD')}
						scale={scale}
					/>
					<DuelDetailRow
						icon={DuelRealMoneyPng}
						label={duelText(t, 'T_SITE_DUELS_ROW_VALUE')}
						value={shortAmountOf(current)}
						scale={scale}
					/>
					{current.requestableUntil && current.canRequest ? (
						<DuelDetailRow
							icon={<DuelIconClockSvg width={22 * scale} height={22 * scale} />}
							label={duelText(t, 'T_SITE_DUELS_ROW_REQUEST_BY')}
							value={formatShortDate(current.requestableUntil)}
							scale={scale}
						/>
					) : null}
				</DuelSection>

				<DuelInfoBox
					text={info ?? duelText(t, 'T_SITE_DUELS_REWARD_INFO')}
					scale={scale}
					width={width}
				/>

				{errorLine(scale)}

				{current.canRequest ? (
					<DuelButton
						label={duelText(t, 'T_SITE_DUELS_REQUEST_REWARD')}
						onPress={() => {
							dispatch(duelRewardTransitionReset());
							setSubmitted(false);
							setStep('form');
						}}
						width={width}
						scale={scale}
					/>
				) : null}
				{current.canCancel ? (
					<DuelButton
						label={duelText(t, 'T_SITE_DUELS_CANCEL_REQUEST')}
						onPress={() => dispatch(duelRewardCancelSubmit(entitlementId))}
						width={width}
						scale={scale}
						variant='dark'
						busy={transition.submitting}
					/>
				) : null}
			</>
		);
	};

	const renderForm = (current: DuelPlayerRewardDto, width: number, scale: number) => (
		<>
			<DuelPanel scale={scale} width={width} style={{ padding: 13 * scale }}>
				<View style={[styles.summary, { columnGap: 12 * scale }]}>
					<Image
						source={DuelRealWorldRewardPng}
						style={{ width: 56 * scale, height: 56 * scale }}
						resizeMode='contain'
					/>
					<View style={[styles.flex, { rowGap: 2 * scale }]}>
						<Text style={[duelTextStyles.title, { fontSize: 16 * scale }]}>
							{amountOf(current)}
						</Text>
						<Text style={[duelTextStyles.body, { fontSize: 12 * scale }]}>
							{duelText(t, 'T_SITE_DUELS_REWARD_SEASON_LINE', [
								current.seasonName,
								formatScore(current.threshold),
							])}
						</Text>
					</View>
				</View>
			</DuelPanel>

			<DuelSection title={duelText(t, 'T_SITE_DUELS_CHOOSE_METHOD')} scale={scale} width={width}>
				<View accessibilityRole='radiogroup' style={{ rowGap: 8 * scale }}>
					{DUEL_REWARD_METHODS.map(option => (
						<DuelMethodOption
							key={option}
							method={option}
							name={rewardMethodText(t, option)}
							hint={rewardMethodText(t, option, 'hint')}
							selected={method === option}
							onPress={() => setMethod(option)}
							scale={scale}
						/>
					))}
				</View>
			</DuelSection>

			<DuelSection title={duelText(t, 'T_SITE_DUELS_ACCOUNT_DETAILS')} scale={scale} width={width}>
				<DuelEmailField
					label={rewardMethodText(t, method, 'email')}
					value={email}
					onChange={setEmail}
					invalid={submitted && !emailValid}
					error={duelText(t, 'T_SITE_DUELS_ERROR_EMAIL')}
					maxLength={EMAIL_MAX}
					scale={scale}
				/>
			</DuelSection>

			<DuelInfoBox text={duelText(t, 'T_SITE_DUELS_ACCOUNT_INFO')} scale={scale} width={width} />

			{errorLine(scale)}

			<DuelButton
				label={duelText(t, 'T_SITE_DUELS_SUBMIT_REQUEST')}
				onPress={submit}
				width={width}
				scale={scale}
				busy={transition.submitting}
			/>
		</>
	);

	const renderSubmitted = (current: DuelPlayerRewardDto, width: number, scale: number) => (
		<>
			<ImageBackground
				source={DuelRequestSubmittedBgPng}
				resizeMode='cover'
				style={[styles.hero, { width, paddingTop: 8 * scale, rowGap: 8 * scale }]}
				imageStyle={{ borderRadius: 12 * scale }}
			>
				<View style={{ width: 280 * scale, height: 200 * scale }}>
					<Image
						source={DuelSuccessGlowPng}
						style={[
							styles.layer,
							{ left: 40 * scale, top: 0, width: 200 * scale, height: 200 * scale },
						]}
						resizeMode='contain'
					/>
					<Image
						source={DuelSuccessConfettiPng}
						style={[
							styles.layer,
							{ left: 16 * scale, top: 20 * scale, width: 248 * scale, height: 173 * scale },
						]}
						resizeMode='contain'
					/>
					<Image
						source={DuelSuccessCheckPng}
						style={[
							styles.layer,
							{ left: 80 * scale, top: 40 * scale, width: 120 * scale, height: 120 * scale },
						]}
						resizeMode='contain'
						accessibilityIgnoresInvertColors
					/>
				</View>
				<Text style={[styles.heroTitle, { fontSize: 28 * scale }]}>
					{duelText(t, 'T_SITE_DUELS_SUBMITTED_HEADLINE')}
				</Text>
				<Text
					style={[
						duelTextStyles.body,
						styles.center,
						{ fontSize: 14 * scale, paddingHorizontal: 24 * scale },
					]}
				>
					{duelText(t, 'T_SITE_DUELS_SUBMITTED_REWARD_BODY', [amountOf(current)])}
				</Text>
			</ImageBackground>

			<DuelSection
				title={duelText(t, 'T_SITE_DUELS_REQUEST_DETAILS_TITLE')}
				scale={scale}
				width={width}
			>
				<DuelDetailRow
					icon={DuelIconRequestIdPng}
					label={duelText(t, 'T_SITE_DUELS_ROW_REQUEST_ID')}
					value={rewardRequestId(current.id)}
					scale={scale}
				/>
				<DuelDetailRow
					icon={DuelGiftBoxPng}
					label={duelText(t, 'T_SITE_DUELS_ROW_REWARD')}
					value={amountOf(current)}
					scale={scale}
				/>
				<DuelDetailRow
					icon={DuelCalendarPng}
					label={duelText(t, 'T_SITE_DUELS_ROW_SEASON')}
					value={current.seasonName}
					scale={scale}
				/>
				<DuelDetailRow
					icon={DuelStarPng}
					label={duelText(t, 'T_SITE_DUELS_ROW_MILESTONE')}
					value={milestoneText(current)}
					scale={scale}
				/>
				<DuelDetailRow
					icon={DuelRealMoneyPng}
					label={duelText(t, 'T_SITE_DUELS_ROW_METHOD')}
					value={rewardMethodText(t, method)}
					scale={scale}
				/>
				<DuelDetailRow
					icon={DuelIconEmailPng}
					label={duelText(t, 'T_SITE_DUELS_ROW_EMAIL')}
					value={email.trim()}
					scale={scale}
				/>
				<DuelDetailRow
					icon={<DuelIconClockSvg width={22 * scale} height={22 * scale} />}
					label={duelText(t, 'T_SITE_DUELS_ROW_STATUS')}
					value={statusPill(current, scale)}
					scale={scale}
				/>
			</DuelSection>

			<DuelInfoBox text={duelText(t, 'T_SITE_DUELS_SUBMITTED_INFO')} scale={scale} width={width} />

			<DuelButton
				label={duelText(t, 'T_SITE_DUELS_VIEW_IN_INVENTORY')}
				onPress={openInventory}
				width={width}
				scale={scale}
			/>
			<DuelButton
				label={duelText(t, 'T_SITE_DUELS_OK')}
				onPress={close}
				width={width}
				scale={scale}
				variant='dark'
			/>
		</>
	);

	return (
		<DuelsPage
			title={pageTitle()}
			onBack={back}
			avoidKeyboard={step === 'form'}
			resetScrollKey={step}
		>
			{({ width, scale }) => {
				if (!reward) {
					if ((!loaded || rewardsLoading) && !loadErrorKey) return <DuelLoading />;
					return (
						<Text style={[duelTextStyles.body, { width, fontSize: 14 * scale }]}>
							{duelText(t, loadErrorKey ?? 'T_SITE_DUELS_REWARD_ERROR_NOT_FOUND')}
						</Text>
					);
				}
				if (step === 'submitted') return renderSubmitted(reward, width, scale);
				if (step === 'form') return renderForm(reward, width, scale);
				return renderDetails(reward, width, scale);
			}}
		</DuelsPage>
	);
};

const styles = StyleSheet.create({
	flex: {
		flex: 1,
		minWidth: 0,
	},
	center: {
		textAlign: 'center',
	},
	summary: {
		flexDirection: 'row',
		alignItems: 'center',
	},
	timelineRow: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
	},
	unlockLine: {
		color: '#FF9C8A',
		fontWeight: '600',
		textAlign: 'center',
	},
	hero: {
		alignItems: 'center',
		paddingBottom: 16,
		overflow: 'hidden',
	},
	layer: {
		position: 'absolute',
	},
	heroTitle: {
		fontWeight: '900',
		color: '#FFE9C2',
		textAlign: 'center',
	},
	error: {
		color: '#FF8B77',
		fontWeight: '600',
	},
});

export default DuelRewardScreen;
